<template>
  <v-container fluid class="pa-6">
    <v-card>
      <v-card-title class="text-h5 pa-6">
        <v-icon large color="primary" class="mr-3">mdi-calendar-plus</v-icon>
        Manual Shift Booking
      </v-card-title>
      <PageExplanation page-key="ManualShiftBooking" />

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" @submit.prevent="bookShift">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.hygienistId"
                label="Hygienist ID"
                type="number"
                :rules="[rules.required]"
                placeholder="Enter hygienist ID"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.locationId"
                label="Location ID"
                type="number"
                :rules="[rules.required]"
                placeholder="Enter location ID"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="formData.overrideReason"
                label="Support override reason"
                :rules="[rules.required, rules.maxReason]"
                counter="1000"
                hint="Required for the durable FFS override audit. Only max-booked, distance, and lead-time limits may be bypassed."
                persistent-hint
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="formData.startDate"
                label="Shift Date"
                type="date"
                :rules="[rules.required]"
                hint="YYYY-MM-DD"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="formData.postedStart"
                label="Start Time"
                :items="timeOptions"
                :rules="[rules.required]"
                hint="Select start time"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="formData.postedEnd"
                label="End Time"
                :items="timeOptions"
                :rules="[rules.required]"
                hint="Select end time"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.hourlyRate"
                label="Hourly Rate ($)"
                type="number"
                :rules="[rules.required, rules.positive]"
                placeholder="50"
                prefix="$"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="formData.paymentTerms"
                :items="paymentTermsOptions"
                label="Payment Terms"
                item-text="text"
                item-value="value"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="formData.paymentMethod"
                :items="paymentMethodOptions"
                label="Payment Method"
                item-text="text"
                item-value="value"
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-alert
                v-if="message"
                :type="messageType"
                dismissible
                @input="message = ''"
                class="mb-4"
              >
                {{ message }}
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                @click="bookShift"
                :loading="loading"
                :disabled="!valid"
                color="primary"
                large
              >
                <v-icon left>mdi-check</v-icon>
                Book Shift
              </v-btn>
              <v-btn
                @click="resetForm"
                :disabled="loading"
                class="ml-3"
                outlined
                large
              >
                <v-icon left>mdi-refresh</v-icon>
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Result Display -->
    <v-card v-if="bookingResult" class="mt-6">
      <v-card-title class="success white--text">
        <v-icon large class="mr-3">mdi-check-circle</v-icon>
        Shift Booked Successfully
      </v-card-title>
      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12" md="6">
            <p><strong>Job ID:</strong> {{ bookingResult.jobId }}</p>
            <p><strong>Shift ID:</strong> {{ bookingResult.shiftId }}</p>
            <p><strong>Audit ID:</strong> {{ bookingResult.auditId }}</p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import config from "@/config";
import { fetchWithAuth } from "@/api";
import {
  MANUAL_BOOKING_PAYMENT_METHOD_OPTIONS,
  PAYMENT_TERM_OPTIONS
} from "@/helpers/paymentTerms";

export default {
  name: "ManualShiftBooking",
  data() {
    return {
      valid: false,
      loading: false,
      message: "",
      messageType: "info",
      bookingResult: null,
      formData: {
        hygienistId: "",
        locationId: "",
        startDate: "",
        postedStart: "",
        postedEnd: "",
        hourlyRate: "",
        paymentTerms: 1,
        paymentMethod: 1,
        eligibilityMode: "support_override",
        overrideReason: ""
      },
      rules: {
        required: value => {
          if (value === null || value === undefined || value === "") {
            return "This field is required";
          }
          if (typeof value === "string" && value.trim() === "") {
            return "This field is required";
          }
          return true;
        },
        positive: value => {
          if (value === null || value === undefined || value === "") {
            return "This field is required";
          }
          const numValue = Number(value);
          if (isNaN(numValue) || numValue <= 0) {
            return "Must be a positive number";
          }
          return true;
        },
        maxReason: value =>
          !value ||
          String(value).length <= 1000 ||
          "Must be 1000 characters or fewer"
      },
      paymentTermsOptions: PAYMENT_TERM_OPTIONS,
      paymentMethodOptions: MANUAL_BOOKING_PAYMENT_METHOD_OPTIONS
    };
  },
  computed: {
    timeOptions() {
      const options = [];
      for (let h = 0; h < 24; h += 1) {
        for (let m = 0; m < 60; m += 15) {
          const period = h < 12 ? "AM" : "PM";
          const hour12 = h % 12 === 0 ? 12 : h % 12;
          const minute = String(m).padStart(2, "0");
          options.push(`${hour12}:${minute} ${period}`);
        }
      }
      return options;
    }
  },
  methods: {
    getFieldLabel(fieldName) {
      const labels = {
        hygienistId: "Hygienist ID",
        locationId: "Location ID",
        startDate: "Shift Date",
        postedStart: "Start Time",
        postedEnd: "End Time",
        hourlyRate: "Hourly Rate",
        paymentTerms: "Payment Terms",
        paymentMethod: "Payment Method",
        overrideReason: "Support override reason"
      };
      return labels[fieldName] || fieldName;
    },
    getEmptyFields() {
      const emptyFields = [];
      const fieldNames = [
        "hygienistId",
        "locationId",
        "startDate",
        "postedStart",
        "postedEnd",
        "hourlyRate",
        "paymentTerms",
        "paymentMethod",
        "overrideReason"
      ];

      fieldNames.forEach(fieldName => {
        const value = this.formData[fieldName];
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          (typeof value === "string" && value.trim() === "")
        ) {
          emptyFields.push(this.getFieldLabel(fieldName));
        }
      });

      return emptyFields;
    },
    async bookShift() {
      const isValid = this.$refs.form.validate();

      if (!isValid) {
        const emptyFields = this.getEmptyFields();
        this.messageType = "error";
        if (emptyFields.length > 0) {
          this.message = `Please fill in all required fields: ${emptyFields.join(
            ", "
          )}`;
        } else {
          this.message =
            "Please fill in all required fields before booking the shift.";
        }
        this.$refs.form.validate();
        return;
      }

      this.loading = true;
      this.message = "";
      this.bookingResult = null;

      try {
        const response = await fetchWithAuth(
          `${config.api_url}v3/admin/manualBooking`,
          {
            method: "POST",
            body: JSON.stringify(this.formData)
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to book shift");
        }

        const result = await response.json();
        this.bookingResult = result;
        this.messageType = "success";
        this.message = "Shift booked successfully! Job and Shift IDs created.";

        // Reset form after successful booking
        setTimeout(() => {
          this.resetForm();
        }, 3000);
      } catch (error) {
        console.error("Booking error:", error);
        this.messageType = "error";
        this.message = error.message || "Failed to book shift";
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      }
      this.bookingResult = null;
      this.message = "";
      this.messageType = "info";
      this.formData = {
        hygienistId: "",
        locationId: "",
        startDate: "",
        postedStart: "",
        postedEnd: "",
        hourlyRate: "",
        paymentTerms: 1,
        paymentMethod: 1,
        eligibilityMode: "support_override",
        overrideReason: ""
      };
    }
  }
};
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
}

.v-btn {
  text-transform: none;
}
</style>
